import React from 'react';

import { MapPin, Phone } from 'lucide-react';

import { CourseMinifiedDTO } from '../../services/apiClient';

interface CourseContactLinksProps {
  course: CourseMinifiedDTO;
  className?: string;
}

// One Google Maps URL for every device: on a desktop it opens the web map, and on a phone the
// operating system hands it to the Google Maps app when it is installed and to the browser when
// it is not.
//
// There was an Apple Maps option here for a moment, on iPhones only, as a small menu under the
// address. It is gone on the user's call: a web page cannot raise the operating system's real
// "open with" chooser, so the menu had to be hand-drawn, and a two-item menu standing between a
// member and a map is worse than simply opening one. It also cost a user-agent sniff to know when
// to offer it. Going straight to Google is one tap, one code path, and nothing to keep working.
//
// **Search by name, not by coordinates.** This used to send `query=<lat>,<lng>`, which is exact
// and useless: Google answers a raw coordinate with an unnamed red pin on an empty map, no club
// name, no photo, no hours, no Directions button. It reads as a page that failed to load, which is
// exactly how it was reported. The same club searched by name comes back as the real listing, with
// its photos and its reviews. Verified side by side on Kleinmond Golf Club.
//
// The coordinates still do a job, as the `@lat,lng,zoom` centre of the search rather than as the
// search itself. That is what keeps "Riverside Golf Club" from landing on a different Riverside on
// another continent: there are 41,119 courses in this database and club names repeat. This is the
// classic /maps/search/ URL shape rather than the documented `?api=1` one, because `?api=1` takes
// a query and nothing else, so it cannot be told where to look. The `?api=1` form is still the
// fallback for a course with no coordinates.
const mapsUrl = (course: CourseMinifiedDTO): string => {
  // Name first, then the address, because the name is what Google matches a business listing on
  // and the address is what narrows it down.
  const text = [course.name, course.address].filter(Boolean).join(', ').trim();

  const hasPoint = course.latitude != null && course.longitude != null;

  // Nothing to search by. Fall back to the bare point, which at least puts the member in the
  // right field even if the pin has no name on it.
  if (!text) {
    return hasPoint
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${course.latitude},${course.longitude}`)}`
      : 'https://www.google.com/maps';
  }

  // 15z is a town-level zoom: close enough to bias the search onto the right club, wide enough
  // that a course whose stored point is a little off is still inside the frame.
  return hasPoint
    ? `https://www.google.com/maps/search/${encodeURIComponent(text)}/@${course.latitude},${course.longitude},15z`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
};

// Shared row styling. Gold rather than the white/50 these rows used to be: the client asked for
// both to be gold by default, and the colour is also what says they are pressable. A link that
// looks exactly like the grey text beside it is a link nobody finds.
const rowClasses =
  'flex gap-2 text-sm text-primary transition-colors hover:text-white focus-visible:outline-none focus-visible:text-white';

const labelClasses =
  'leading-snug underline decoration-primary/30 underline-offset-2';

// The club's address and phone number, both as links, on every device.
//
// The address opens a map. The phone dials. About half the active courses have a usable number;
// the rest of that row simply is not rendered. Numbers are cleaned server-side at snapshot build,
// because the stored column is a pipe-delimited list with embedded tabs (see
// CourseService.CleanPhoneNumber), and the spaces are stripped again here because a `tel:` href
// with spaces in it is not reliably dialled.
const CourseContactLinks: React.FC<CourseContactLinksProps> = ({
  course,
  className = '',
}) => (
  <div className={`flex flex-col gap-2 ${className}`}>
    {course.address && (
      <a
        href={mapsUrl(course)}
        target="_blank"
        rel="noopener noreferrer"
        // The club is named in the label rather than the raw address, because a screen reader
        // reading out a full South African postal address as a link name is a mouthful nobody
        // needs to hear before deciding whether to follow it.
        aria-label={`Open ${course.name ?? 'this course'} in Google Maps`}
        className={`${rowClasses} items-start`}
      >
        <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
        <span className={labelClasses}>{course.address}</span>
      </a>
    )}

    {course.phoneNumber && (
      <a
        href={`tel:${course.phoneNumber.replace(/\s+/g, '')}`}
        aria-label={`Call ${course.name ?? 'this course'}`}
        className={`${rowClasses} items-center`}
      >
        <Phone className="h-4 w-4 shrink-0" />
        <span className={labelClasses}>{course.phoneNumber}</span>
      </a>
    )}
  </div>
);

export default CourseContactLinks;
